import React, { useEffect, useRef, useState } from "react";
import { KeyValue } from "@/main/models/common";
import SecondaryButton from "../../button/secondaryButton";
import * as S from './styles';
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import KOGAutoCompleteCheckbox from "../../inputAutoCompleteCheck";
import KOGInsurerCompanyBoxGridMultiCheckbox, { KOGInsurerCompanyBoxGridMultiCheckboxRef } from "../insurerCompanyBoxGridMultiCheckbox/insurerCompanyBoxGridMultiCheckbox";
import { supervisionAssignmentApi } from "@/main/api/supervisionAssignmentApi";
import { supervisionAssignmentFilterRequest, supervisionAssignmentRequest } from "@/main/models/request/supervisionAssignment";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import KogAutoCompleteControlled from "../../inputAutoComplete/autoCompleteControlled";
import useFormQueryParams from "@/main/hooks/useformQueryParams";
import { toast } from "react-toastify";
import useAsyncModalConfirmReason from "../../asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";


interface IRegisterSupervisorAssignmentProps{
    guidCompany: string,
    guidInsurer?: string,
    initialFilters: supervisionAssignmentFilterRequest | null;
    insurers: KeyValue[],
    bodyShops: KeyValue[],
    isAdmin: boolean,
    isLoad: boolean,
    setIsLoad: React.Dispatch<React.SetStateAction<boolean>>,
    selectInsurer: KeyValue[],
    setSelectInsurer: React.Dispatch<React.SetStateAction<KeyValue[]>>,
    save: () => void,
    resetSearch: () => void,
    intitialTotalItems: number,
}

function RegisterSupervisorAssignment({
    guidCompany,
    guidInsurer,
    insurers,
    bodyShops,
    initialFilters,
    isAdmin,
    isLoad,
    setIsLoad,
    selectInsurer,
    setSelectInsurer,
    save,
    resetSearch,
    intitialTotalItems
    
} : IRegisterSupervisorAssignmentProps){

  interface IGridData {
    guid: string;
    value: string;
  }

  const INITIAL_GRID_STATE: IGridData = {
    guid: '',
    value: ''
  };
  

  //#region states
  const [dataInsurer, setDataInsurer] = useState<KeyValue[]>([]);
  const gridRef = useRef<KOGInsurerCompanyBoxGridMultiCheckboxRef>(null);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
  const [isHeader, setIsHeader] = useState<boolean>(false);
  const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

  
  const INITIAL_FORM_STATE = {
    insurer: { key: guidInsurer, value: '' }
  };
  type ISearchValuatorForm = typeof INITIAL_FORM_STATE;

  const methods = useFormQueryParams<ISearchValuatorForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
    onSubmit: onSave
  });
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = methods;
  //#endregion

  const bodyShopsLength = bodyShops?.length ?? 0;
  const insurersLength = insurers?.length ?? 0;

  useEffect(() => {
    const hasHeader = bodyShopsLength > 0 || insurersLength > 0;

    setIsHeader(prev => (prev !== hasHeader ? hasHeader : prev));
    setIsSaveDisabled(true);
    setDataInsurer(insurers ?? []);
  }, [bodyShopsLength, insurersLength]);

  useEffect(() => {
    if (!isAdmin) return;

    const selectBodyshops = gridRef.current?.getSelectedItems();
    const isSelectedInsurer = selectInsurer.length > 0;
    const isSelectedBodyshop =
      Array.isArray(selectBodyshops) && selectBodyshops.length > 0;

    setIsSaveDisabled(!(isSelectedInsurer && isSelectedBodyshop));
  }, [selectInsurer.length, isAdmin]);


  //#region functions
  async function onSave(){
    const selectBodyshops = gridRef.current?.getSelectedItems();
    const selectDropInsurer = isAdmin ? selectInsurer : [getValues().insurer];

    if(isNullUndefined(selectBodyshops)) {
      const message = (isAdmin 
        ? getTranslateByKey('information.selectA') 
        : getTranslateByKey('information.selectOne'))
      .replace('{0}', getTranslateByKey('information.bodyshop'));

      toast.warning(message);
      return;
    }

    if(isNullUndefined(selectDropInsurer)) {
      const message = (isAdmin 
        ? getTranslateByKey('information.selectOne') 
        : getTranslateByKey('information.selectA'))
      .replace('{0}', getTranslateByKey('information.insurer'));
        
      toast.warning(message);
      return;
    }

    const request: supervisionAssignmentRequest = {
      guidCompany: guidCompany,
      guidInsurer: isAdmin ? selectInsurer.map(insurer => String(insurer.key)) : [String(getValues().insurer?.key)],
      guidBodyShop: selectBodyshops
    }

    const resultConfirm = await getConfirmationReason(
      getTranslateByKey('information.confirmChangeTitle'),
      ReasonTypeEnum.ChangeCompany
    );

    if (!resultConfirm.confirmed) return;

    setIsLoad(true);

    let filesRequest = undefined;
    if (resultConfirm.files && resultConfirm.files.length > 0) {
      filesRequest = await Promise.all(
        resultConfirm.files.map(async item => {
          const base64DataUrl = await toBase64(item);
          return {
            fileName: item.name,
            contentType: item.type,
            fileBase64: base64DataUrl as string
          };
        })
      );
    }

    request.changeJustification = {
      guidReason: resultConfirm.reason ? String(resultConfirm.reason.key) : null,
      reasonDescription: resultConfirm.observation || null,
      filesUpload: filesRequest
    };

    supervisionAssignmentApi
      .saveSupervisorAssignments(request)
      .finally(() => {
        toast.success(getTranslateByKey("information.myBodyshopsSavedSuccessfully"))
        setIsLoad(false);
        setDataInsurer([]);
        resetSearch();
        save();
      });
  }

  function loadOptions(){
    setDataInsurer(insurers);
  }

  function changedDisabledButton(){
    const selectBodyshops = gridRef.current?.getSelectedItems();
    const selectDropInsurer = isAdmin ? selectInsurer : [getValues().insurer];

    const isSelectedInsurer = isAdmin ? selectDropInsurer.length > 0 : true;
    const isSelectedBodyshop = !isNullUndefined(selectBodyshops) && selectBodyshops.length > 0;

    setIsSaveDisabled(!(isSelectedInsurer && isSelectedBodyshop));
  }

  const handleLoadInsurers = async (guidBodyShop: string): Promise<KeyValue[]> => {
    setSelectInsurer([]);
    setDataInsurer([]);
    
    if(guidBodyShop === '')
        return [];

    var filters: supervisionAssignmentFilterRequest = {
      guidCompany: guidCompany,
      guidBodyshop: guidBodyShop,
      pageIndex: 1,
      pageSize: PaginationEnum.PageSize15
    }

    supervisionAssignmentApi
      .getbodyshopinsurerbyfilter(filters)
      .then(res => {
        const result = res.data?.result;
        setDataInsurer(result.insurers?.results);
      })
      .catch(error => {
        setDataInsurer([]);
      });

    return dataInsurer;
  };
  //#endregion

  return(
    <>
    {isHeader && (
        <form onSubmit={onSave} noValidate>
          {isAdmin ? (
              <>
                <KOGInsurerCompanyBoxGridMultiCheckbox
                    ref={gridRef}
                    rowHeaders={[getTranslateByKey('information.rfcBodyshop')]}
                    dataBodyshops={bodyShops}
                    isLoading={isLoad}
                    singleSelect={true}
                    guidCompany={guidCompany}
                    guidInsurer={guidInsurer}
                    initialFilters={initialFilters}
                    intialTotalItems={intitialTotalItems}
                    onChanged={(guidBodyshop) => {
                      handleLoadInsurers(guidBodyshop);
                    }}
                    onEventCallback={() => {
                      setTimeout(() => {
                        changedDisabledButton();
                      }, 0);
                    }}/>

                <S.AutoCompleteField>
                  <KOGAutoCompleteCheckbox 
                    label={getTranslateByKey('information.insurer')}
                    isLoading={isLoad}
                    options={dataInsurer}
                    valueCustom={selectInsurer}
                    setValueCustom={setSelectInsurer}
                    selectAllLabel={getTranslateByKey("information.selectAll")}
                    customInputProps={{
                      required: true,
                      id: 'id-assingnmet-insurer'
                    }}
                    />
                </S.AutoCompleteField>
              </>
            ) 
            : (
              <>
                <KOGInsurerCompanyBoxGridMultiCheckbox
                    ref={gridRef}
                    rowHeaders={[getTranslateByKey('information.rfcBodyshop')]}
                    dataBodyshops={bodyShops}
                    guidCompany={guidCompany}
                    guidInsurer={guidInsurer}
                    isLoading={isLoad}
                    initialFilters={initialFilters}
                    intialTotalItems={intitialTotalItems}
                    singleSelect={false}
                    onEventCallback={() => {
                      setTimeout(() => {
                        changedDisabledButton();
                      }, 0);
                    }}/>

                <S.AutoCompleteField>
                  <KogAutoCompleteControlled
                    label={getTranslateByKey('information.insurer')}
                    nameCustom="insurer"
                    isLoading={isLoad}
                    disabled={true}
                    options={dataInsurer}
                    isDefaultValueUnique={true}
                    control={control}
                    id= 'id-assingnmet-insurer'
                    setValueCustom={setValue}
                    onChangeCustom={(optionSelected: KeyValue | null) => {}}/>
                </S.AutoCompleteField>
              </>
            )}

            <S.AreaButtons>
              <SecondaryButton
                id="id-SupervisionAssingnment-button-save"
                type="submit"
                isLoading={isLoad}
                disabled={isSaveDisabled}>
                  {getTranslateByKey('information.save')}
              </SecondaryButton>
            </S.AreaButtons>
        </form>
      )}
      <ModalConfirmationReason />
    </>      
  )
}

export default RegisterSupervisorAssignment;
